import {
  Component,
  inject,
  signal,
  computed,
  HostListener,
  ElementRef,
  OnInit,
  OnDestroy,
  effect,
  afterNextRender,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  CdkMenuTrigger,
  CdkMenu,
  CdkMenuItem,
} from '@angular/cdk/menu';
import { FormsModule } from '@angular/forms';

import { MatIconModule }   from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule }  from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PopupComponent }   from '@libs/front/components/popup/popup.component';
import { ButtonComponent }  from '@libs/front/components/button/button.component';
import { RadioBoxComponent, RadioIconDirective } from '@libs/front/components/radiobox/radiobox.component';
import { AutocompleteComponent, TreeNodeAutoComplete } from '@libs/front/components/autocomplete/autocomplete.component';

import { SharedOrganizationService } from 'src/app/services/organization.service';
import { DeviceService } from 'src/app/services/device.service';
import { TraductionService } from 'src/app/services/traduction.service';
import { CookieService } from 'ngx-cookie-service';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { KeycloakUtilisateurService } from 'src/app/services/keycloak/keycloak-utilisateur.service';
import { CONFIG_ROLES } from 'src/app/features/config/config-roles';
import { FeatureFlagService } from 'src/app/services/feature-flag.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { TranslateModule } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-nav-bar',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    CdkMenuTrigger, CdkMenu, CdkMenuItem,
    MatBadgeModule, MatButtonModule, MatIconModule, MatTooltipModule,
    TranslateModule,
    PopupComponent, ButtonComponent, RadioBoxComponent, RadioIconDirective,
    AutocompleteComponent
  ],
  templateUrl: './nav-bar.component.html',
  styleUrls: ['./nav-bar.component.scss'],
})
export class NavBarComponent implements OnInit, OnDestroy {

  readonly userProfile = toSignal(
    this.keycloakUtilisateurService.userProfile$,
    { initialValue: this.keycloakUtilisateurService.userProfile ?? null }
  );

  readonly isOperations = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this._isOperationsUrl(this.router.url)),
      startWith(this._isOperationsUrl(this.router.url))
    ),
    { initialValue: this._isOperationsUrl(this.router.url) }
  );

  private _isOperationsUrl(url: string): boolean {
    return url === '/area' || url === '/site' || url.startsWith('/site/');
  }

  readonly isOurProducts = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this._isOurProductsUrl(this.router.url)),
      startWith(this._isOurProductsUrl(this.router.url))
    ),
    { initialValue: this._isOurProductsUrl(this.router.url) }
  );

  private _isOurProductsUrl(url: string): boolean {
    return url.startsWith('/our-products');
  }

  // ── Configuration ────────────────────────────────────────────────────────────
  private readonly featureFlags = inject(FeatureFlagService);

  readonly canSeeConfig = computed(
    () => this.featureFlags.showNewConfiguration() && this.keycloakUtilisateurService.hasAnyRole(CONFIG_ROLES)
  );

  readonly isConfig = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this._isConfigUrl(this.router.url)),
      startWith(this._isConfigUrl(this.router.url))
    ),
    { initialValue: this._isConfigUrl(this.router.url) }
  );

  private _isConfigUrl(url: string): boolean {
    const path = url.split('?')[0];
    return path === '/new_config' || path.startsWith('/new_config/');
  }

  navigateToConfig(): void {
    this.router.navigate(['/new_config']);
    this.menuOpen.set(false);
  }

  // Liste des organisations (dropdown, visible uniquement en Configuration)
  private readonly sharedOrg = inject(SharedOrganizationService);
  private readonly organizations = toSignal(this.sharedOrg.organizations$, { initialValue: [] });
  
  readonly orgOptions = computed<TreeNodeAutoComplete[]>(
    () => this.organizations().map(o => ({ id: o.orgId, label: o.name }))
  );
  private readonly selectedOrg = toSignal(this.sharedOrg.selectedOrganization$, { initialValue: null });
  readonly selectedOrgLabel = computed<string>(() => this.selectedOrg()?.name ?? '');

  onOrgSelected(node: TreeNodeAutoComplete): void {
    const org = this.sharedOrg.getOrganizations().find(o => o.orgId === node.id);
    if (org) this.sharedOrg.setSelectedOrganization(org);
  }

  // ── Largeur du bouton profile → min-width du menu ─────────────────────────────
  readonly profileBtnWidth = signal<string>('auto');
  private _profileBtnObserver?: ResizeObserver;

  notif = true;
  
  constructor(
    private readonly traductionService: TraductionService,
    private readonly cookieService: CookieService,
    private readonly keycloakUtilisateurService: KeycloakUtilisateurService,
    private readonly authService: AuthenticationService,
    private readonly router: Router
  ) {
    // Après le premier rendu — le bouton .profile-btn est dans le DOM
    afterNextRender(() => this._observeProfileBtn());

    // Quand le profil utilisateur arrive (async) → relire la largeur du bouton
    effect(() => {
      this.userProfile();
      setTimeout(() => this._observeProfileBtn(), 0);
    });
  }

  ngOnInit(): void {
    const langCookie = this.cookieService.get('epcLanguage');
    this.languageSelected = langCookie ? langCookie : 'en';
    this.buildLanguages();
    this._langChangeSub = this.traductionService.translate.onLangChange
      .subscribe(() => this.buildLanguages());
  }

  private buildLanguages(): void {
    const t = this.traductionService.translate;
    this.languages = [
      { value: 'en', label: t.instant('MODALE_CHANGER_LANGUE.ANGLAIS') },
      { value: 'fr', label: t.instant('MODALE_CHANGER_LANGUE.FRANCAIS') },
      { value: 'es', label: t.instant('MODALE_CHANGER_LANGUE.ESPAGNOL') },
    ];
  }

  protected device = inject(DeviceService);

  private readonly elementRef = inject(ElementRef);

  // ── État UI ──────────────────────────────────────────────────────────────────
  readonly menuOpen     = signal(false);
  profileMenuOpen       = false;
  isPopupOpen           = false;

  // ── Données ──────────────────────────────────────────────────────────────────
  languages: { value: string; label: string }[] = [];
  languageSelected = 'en';

  private _resizeObserver?: ResizeObserver;
  private _langChangeSub?: Subscription;

  ngOnDestroy(): void {
    this._resizeObserver?.disconnect();
    this._profileBtnObserver?.disconnect();
    this._langChangeSub?.unsubscribe();
  }

  // ── Lecture largeur du bouton profile ─────────────────────────────────────────
  private _observeProfileBtn(): void {
    const btn = this.elementRef.nativeElement.querySelector('.profile-btn') as HTMLElement;
    if (!btn) return;

    // Lire la largeur immédiatement
    this._setProfileBtnWidth(btn);

    // Observer les changements de taille (nom chargé async, resize, etc.)
    this._profileBtnObserver?.disconnect();
    this._profileBtnObserver = new ResizeObserver(() => this._setProfileBtnWidth(btn));
    this._profileBtnObserver.observe(btn);
  }

  private _setProfileBtnWidth(btn: HTMLElement): void {
    const w = btn.offsetWidth;
    if (w > 0) this.profileBtnWidth.set(`${w}px`);
  }

  // ── Actions ──────────────────────────────────────────────────────────────────
  openPopup(): void { this.isPopupOpen = true;  }
  onClose():   void { this.isPopupOpen = false; this.menuOpen.set(false); }

  toggleMenu(): void { this.menuOpen.update(v => !v); }
  closeMenu():  void { this.menuOpen.set(false); }

  onProfileMenu(): void { this.profileMenuOpen = !this.profileMenuOpen; }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.profileMenuOpen) return;
    const target        = event.target as HTMLElement;
    const profileButton = this.elementRef.nativeElement.querySelector('.profile-btn');
    const profileMenu   = this.elementRef.nativeElement.querySelector('.menu-tab');
    if (
      profileButton && !profileButton.contains(target) &&
      (!profileMenu  || !profileMenu.contains(target))
    ) {
      this.profileMenuOpen = false;
    }
  }

  changerLangue(): void {
    this.traductionService.changerLangue(this.languageSelected, true);
    this.isPopupOpen = false;
    this.menuOpen.set(false);
  }

  logout() {
    this.authService.logout();
    this.menuOpen.set(false);
  }

  navigateToProfile(): void {
    this.router.navigate(['/new-profile']);
    this.menuOpen.set(false);
  }

  navigateToArea(): void {
    this.router.navigate(['/area']);
    this.menuOpen.set(false);
  }

  navigateToOurProducts(): void {
    this.router.navigate(['/our-products']);
    this.menuOpen.set(false);
  }

  closePopup(): void {
    this.isPopupOpen = false;
    const langCookie = this.cookieService.get('epcLanguage');
    this.languageSelected = langCookie ? langCookie : 'en';
  }
}